// mddExport.js — Excel sheet + compaction-curve graph for MDD / OMC (IS 2720 Part 8:1983).
// One column per trial, like the FDD sheet. Runs in the browser; the portal calls mddExporter.addSheet().
const THIN = { style: 'thin', color: { argb: 'FF000000' } };
const BOX = { top: THIN, left: THIN, bottom: THIN, right: THIN };
const font = (size = 12, bold = false) => ({ name: 'Calibri', size, bold });
const utcDate = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};

// ---------------------------------------------------------------- graph
// Dry density (y) vs water content (x): trial points, fitted curve, peak lines, optional zero-air-voids line.
export function drawCompactionCurve(canvas, { points, coef, mdd, omc, gs, title }) {
  const W = 1240, H = 800;
  canvas.width = W; canvas.height = H;
  const g = canvas.getContext('2d');
  g.fillStyle = '#ffffff'; g.fillRect(0, 0, W, H);
  const m = { l: 140, r: 60, t: 100, b: 130 };

  const ws = points.map((p) => p.w), ds = points.map((p) => p.dry);
  const xLo = Math.floor(Math.min(...ws) - 1), xHi = Math.ceil(Math.max(...ws) + 1);
  const zav = (w) => gs / (1 + (w / 100) * gs);
  let yLo = Math.min(...ds) - 0.05, yHi = Math.max(...ds, mdd) + 0.05;
  if (gs) yHi = Math.max(yHi, Math.min(zav(xLo), yHi + 0.15)); // show ZAV only as far as it is useful
  const ys = 0.05; // 0.05 g/cc grid
  yLo = Math.floor(yLo / ys) * ys; yHi = Math.ceil(yHi / ys) * ys;
  const px = (w) => m.l + ((w - xLo) / (xHi - xLo)) * (W - m.l - m.r);
  const py = (d) => H - m.b - ((d - yLo) / (yHi - yLo)) * (H - m.t - m.b);

  g.font = 'bold 34px Arial'; g.fillStyle = '#111'; g.textAlign = 'center'; g.fillText(title, W / 2, 55);
  g.lineWidth = 1; g.strokeStyle = '#d9d9d9'; g.fillStyle = '#333'; g.font = '24px Arial';
  const xStep = xHi - xLo <= 12 ? 1 : 2;
  for (let w = xLo; w <= xHi + 1e-9; w += xStep) {
    g.beginPath(); g.moveTo(px(w), m.t); g.lineTo(px(w), H - m.b); g.stroke();
    g.textAlign = 'center'; g.fillText(String(w), px(w), H - m.b + 34);
  }
  for (let d = yLo; d <= yHi + 1e-9; d += ys) {
    g.beginPath(); g.moveTo(m.l, py(d)); g.lineTo(W - m.r, py(d)); g.stroke();
    g.textAlign = 'right'; g.fillText(d.toFixed(2), m.l - 14, py(d) + 8);
  }
  g.strokeStyle = '#111'; g.lineWidth = 2.5; g.strokeRect(m.l, m.t, W - m.l - m.r, H - m.t - m.b);
  g.fillStyle = '#111'; g.font = '28px Arial'; g.textAlign = 'center';
  g.fillText('Water content (%)', (m.l + W - m.r) / 2, H - 30);
  g.save(); g.translate(44, (m.t + H - m.b) / 2); g.rotate(-Math.PI / 2); g.fillText('Dry density (g/cc)', 0, 0); g.restore();

  g.save();
  g.beginPath(); g.rect(m.l, m.t, W - m.l - m.r, H - m.t - m.b); g.clip();
  if (gs) { // zero-air-voids
    g.strokeStyle = '#6b7280'; g.lineWidth = 3; g.setLineDash([4, 8]); g.beginPath();
    for (let w = xLo; w <= xHi + 1e-9; w += 0.25) (w === xLo ? g.moveTo(px(w), py(zav(w))) : g.lineTo(px(w), py(zav(w))));
    g.stroke(); g.setLineDash([]);
  }
  // fitted parabola over the tested range
  const f = (w) => coef[0] + coef[1] * w + coef[2] * w * w;
  const w0 = Math.min(...ws), w1 = Math.max(...ws);
  g.strokeStyle = '#1d4ed8'; g.lineWidth = 4; g.beginPath();
  for (let i = 0; i <= 80; i++) { const w = w0 + ((w1 - w0) * i) / 80; (i ? g.lineTo(px(w), py(f(w))) : g.moveTo(px(w), py(f(w)))); }
  g.stroke();
  // peak construction
  g.setLineDash([14, 10]); g.strokeStyle = '#b91c1c'; g.lineWidth = 3;
  g.beginPath(); g.moveTo(px(omc), H - m.b); g.lineTo(px(omc), py(mdd)); g.lineTo(m.l, py(mdd)); g.stroke(); g.setLineDash([]);
  g.fillStyle = '#111'; points.forEach((p) => { g.beginPath(); g.arc(px(p.w), py(p.dry), 10, 0, Math.PI * 2); g.fill(); });
  g.restore();

  g.fillStyle = '#b91c1c'; g.font = 'bold 28px Arial'; g.textAlign = 'left';
  g.fillText(`MDD = ${mdd.toFixed(2)} g/cc`, m.l + 24, m.t + 44);
  g.fillText(`OMC = ${omc.toFixed(1)} %`, m.l + 24, m.t + 84);
  if (gs) { g.fillStyle = '#4b5563'; g.font = '22px Arial'; g.textAlign = 'right'; g.fillText(`dotted: zero air voids (Gs = ${gs})`, W - m.r - 16, m.t + 34); }
  return canvas;
}

// ---------------------------------------------------------------- workbook
export function addMDDSheet(wb, { header, inputs, result, imageBase64 }) {
  const trials = inputs.trials, nT = trials.length;
  const last = 1 + nT;                       // A label | B.. trials
  const ws = wb.addWorksheet('MDD', {
    pageSetup: { paperSize: 9, orientation: 'portrait', fitToPage: true, fitToWidth: 1, fitToHeight: 1 },
  });
  ws.getColumn(1).width = 44;
  for (let c = 2; c <= last; c++) ws.getColumn(c).width = 17;
  const col = (c) => ws.getColumn(c).letter;
  const left = { horizontal: 'left', vertical: 'middle', wrapText: true };
  const center = { horizontal: 'center', vertical: 'middle', wrapText: true };
  const area = (r1, c1, r2, c2, value, o = {}) => {
    for (let r = r1; r <= r2; r++) for (let c = c1; c <= c2; c++) {
      const cell = ws.getCell(r, c);
      cell.border = o.border ?? BOX; cell.font = o.font ?? font(); cell.alignment = o.align ?? center;
    }
    const top = ws.getCell(r1, c1);
    if (value !== undefined && value !== null && value !== '') top.value = value;
    if (o.numFmt) top.numFmt = o.numFmt;
    if (r2 > r1 || c2 > c1) ws.mergeCells(r1, c1, r2, c2);
  };
  let r = 0;
  const single = (label, value, numFmt, bold = false) => {
    r += 1; ws.getRow(r).height = 26;
    area(r, 1, r, 1, label, { font: font(13, true), align: left });
    area(r, 2, r, last, value, { font: font(13, bold), numFmt });
    return r;
  };
  const perTrial = (label, valueFor, numFmt, o = {}) => {
    r += 1; ws.getRow(r).height = 26;
    area(r, 1, r, 1, label, { font: font(13, !!o.boldLabel), align: left });
    trials.forEach((t, i) => area(r, 2 + i, r, 2 + i, valueFor(t, i, col(2 + i)), { font: font(13, !!o.bold), numFmt }));
    return r;
  };

  r = 1; ws.getRow(1).height = 34;
  area(1, 1, 1, 1, 'Name of Project:-', { font: font(13, true), align: left });
  area(1, 2, 1, last, header.project, { font: font(12), align: left });
  single('Issue to', header.issueTo);
  r += 1; area(r, 1, r, last, 'COMPACTION TEST (MDD / OMC) — Heavy compaction, as per IS : 2720 (Part 8) : 1983', { font: font(13, true) }); ws.getRow(r).height = 28;
  single('Type of Material', header.material);
  if (header.source) single('Kind of material / source', header.source);
  single('Location / Offset', header.location);
  single('Sample No.', header.sampleNo);
  if (header.sampledOn) single('Date of Sampling', utcDate(header.sampledOn), 'dd/mm/yyyy');
  single('Date of Receiving', utcDate(header.receivedOn), 'dd/mm/yyyy');
  single('Date of Testing', utcDate(header.testedOn), 'dd/mm/yyyy');
  single('Rammer / layers / blows', `4.9 kg rammer · 5 layers · ${result.mould.blows} blows per layer`);
  const rVol = single('Volume of mould (cc)', result.mould.volumeCc, '0');
  if (inputs.mouldNo != null) single('Mould no.', inputs.mouldNo, '0');
  const rMw = single('Weight of mould (gms)', inputs.mouldMassG, '0');
  single('Procedure', result.procedure === 'single' ? 'Single sample' : 'Separate samples');
  single('% retained on 19 mm sieve', result.retained19Pct, '0');

  r += 1; ws.getRow(r).height = 26;
  area(r, 1, r, 1, 'Trial no.', { font: font(13, true), align: left });
  trials.forEach((_, i) => area(r, 2 + i, r, 2 + i, i + 1, { font: font(13, true) }));

  const rMs = perTrial('Weight of mould + compacted soil (gms)', (t) => t.mouldSoilG, '0');
  const rSoil = perTrial('Weight of compacted soil (gms)', (t, i, L) => ({ formula: `${L}${rMs}-$B$${rMw}`, result: t.mouldSoilG - inputs.mouldMassG }), '0');
  const rWet = perTrial('Wet density (g/cc)', (t, i, L) => ({ formula: `${L}${rSoil}/$B$${rVol}`, result: result.points[i].bulk }), '0.000');
  if (trials.some((t) => t.contNo != null)) perTrial('Container no.', (t) => t.contNo ?? '', '0');
  const rCw = perTrial('Container + wet soil (gms)', (t) => t.wetG, '0.00');
  const rCd = perTrial('Container + dry soil (gms)', (t) => t.dryG, '0.00');
  const rWat = perTrial('Weight of water, w8 = w6 − w7 (gms)', (t, i, L) => ({ formula: `${L}${rCw}-${L}${rCd}`, result: t.wetG - t.dryG }), '0.00');
  const rCt = perTrial('Container (gms)', (t) => t.tareG, '0.00');
  const rDm = perTrial('Weight of dry soil, w9 = w7 − w5 (gms)', (t, i, L) => ({ formula: `${L}${rCd}-${L}${rCt}`, result: t.dryG - t.tareG }), '0.00');
  const rMc = perTrial('Moisture content (%)', (t, i, L) => ({ formula: `${L}${rWat}/${L}${rDm}*100`, result: result.points[i].w }), '0.00');
  perTrial('Dry density (g/cc)', (t, i, L) => ({ formula: `${L}${rWet}*100/(100+${L}${rMc})`, result: result.points[i].dry }), '0.000', { bold: true, boldLabel: true });

  r += 1; ws.getRow(r).height = 30;
  area(r, 1, r, 1, 'Maximum Dry Density, MDD (g/cc)', { font: font(14, true), align: left });
  area(r, 2, r, last, result.mdd, { font: font(14, true), numFmt: '0.00' });
  r += 1; ws.getRow(r).height = 30;
  area(r, 1, r, 1, 'Optimum Moisture Content, OMC (%)', { font: font(14, true), align: left });
  area(r, 2, r, last, result.omc, { font: font(14, true), numFmt: '0.0' });

  if (imageBase64) {
    const id = wb.addImage({ base64: imageBase64, extension: 'png' });
    ws.addImage(id, { tl: { col: 0, row: r + 1 }, ext: { width: 640, height: 413 } });
  }
  r += 23; // room for the graph (~22 rows)
  area(r, 1, r, last, 'MDD and OMC are read from a parabola fitted to the points around the peak, rounded per IS 2720-8 (MDD to 0.01 g/cc; OMC to 0.2 / 0.5 / 1 % by range). Dry density = wet density × 100 ÷ (100 + moisture content).',
    { font: font(9), align: left, border: {} });
  ws.getRow(r).height = 26;
  r += 1; area(r, 1, r, last, `Checked by: ${header.checkedBy}   |   Tested by: ${header.testedBy}`, { font: font(9), align: left, border: {} });
  r += 2; ws.getRow(r).height = 44;
  area(r, 1, r, last, 'Authorised Signatory', { font: font(12, true), align: { horizontal: 'right', vertical: 'middle' }, border: {} });
  return ws;
}

export const mddExporter = {
  async addSheet(wb, { header, data }) {
    const { inputs, result } = data;
    const canvas = drawCompactionCurve(document.createElement('canvas'), {
      points: result.points, coef: result.coef, mdd: result.mddRaw, omc: result.omcRaw, gs: inputs.gs,
      title: `Compaction curve — ${header.sampleNo || ''} ${header.location || ''}`.trim(),
    });
    addMDDSheet(wb, { header, inputs, result, imageBase64: canvas.toDataURL('image/png') });
    const png = await new Promise((res) => canvas.toBlob(res, 'image/png'));
    return [{ suffix: 'MDD_compaction_curve.png', blob: png }];
  },
};
